import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin, Heart, Clock } from 'lucide-react'
import { getClasses } from '../data'
import { getContactEmail, getContactEmailLink, getStudioPhone, socialMediaConfig } from '../core'

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const classes = getClasses()

  // Generate class links dynamically from actual data
  const classCategories = classes.slice(0, 6).map((danceClass) => ({
    name: danceClass.name,
    href: `/classes/${danceClass.id}`,
    icon: <Heart className="w-4 h-4" />
  }))

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Classes', href: '/classes' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ]

  // Map social media config to include icons
  const socialLinks = socialMediaConfig.slice(0, 3).map(social => ({
    ...social,
    icon: social.icon === 'instagram' ? <Instagram className="w-5 h-5" /> :
          social.icon === 'facebook' ? <Facebook className="w-5 h-5" /> :
          social.icon === 'youtube' ? <Youtube className="w-5 h-5" /> :
          <Instagram className="w-5 h-5" /> // fallback
  }))

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="bg-gray-900 dark:bg-gray-950 text-white"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Studio Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">DS</span>
              </div>
              <div>
                <h3 className="text-lg font-bold">Dance with Dr. Sophie</h3>
                <p className="text-sm text-gray-400">Professional Dance Studio</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm">
              Master the art of dance with expert instruction from Dr. Sophia Mohan. 
              From classical forms to modern styles, discover your rhythm and express yourself through movement.
            </p>
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <Clock className="w-4 h-4" />
              <span>Mon-Fri: 9AM-8PM, Sat-Sun: 10AM-6PM</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href} 
                    className="text-gray-400 hover:text-white transition-colors flex items-center"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Classes */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">Our Classes</h4>
            <ul className="space-y-2">
              {classCategories.map((classItem, index) => (
                <li key={index}>
                  <Link 
                    to={classItem.href} 
                    className="text-gray-400 hover:text-white transition-colors flex items-center"
                  >
                    <span className="mr-2">{classItem.icon}</span>
                    {classItem.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold">Contact Info</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <MapPin className="w-4 h-4 text-primary-400" />
                <span className="text-gray-400 text-sm">Bangalore, Karnataka, India</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-4 h-4 text-primary-400" />
                <a href={`tel:${getStudioPhone().replace(/\s/g, '')}`} className="text-gray-400 hover:text-white transition-colors text-sm">
                  {getStudioPhone()}
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-primary-400" />
                <a href={getContactEmailLink()} className="text-gray-400 hover:text-white transition-colors text-sm">
                  {getContactEmail()}
                </a>
              </div>
            </div>
            
            {/* Social Links */}
            <div className="pt-4">
              <h5 className="text-sm font-semibold mb-3">Follow Us</h5>
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <a 
                    key={index}
                    href={social.href} 
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-gray-400 ${social.color} transition-colors`}
                    title={social.name}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Signup
        <div className="border-t border-gray-800 dark:border-gray-700 mt-8 pt-8">
          <div className="max-w-md mx-auto text-center">
            <h4 className="text-lg font-semibold mb-2">Stay Updated</h4>
            <p className="text-gray-400 text-sm mb-4">
              Subscribe to get updates about new classes and special events
            </p>
            <div className="flex space-x-2">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 bg-gray-800 dark:bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </div> */}

        <div className="border-t border-gray-800 dark:border-gray-700 mt-8 pt-8 text-center">
          <p className="text-gray-400 text-sm">
            &copy; {currentYear} Dance with Dr. Sophie. All rights reserved. | 
            <Link to="/privacy" className="hover:text-white transition-colors ml-1">Privacy Policy</Link> | 
            <Link to="/terms" className="hover:text-white transition-colors ml-1">Terms of Service</Link>
          </p>
        </div>
      </div>
    </motion.footer>
  )
}
